import {
  canOpenFence,
  closesFence,
  getFenceMatch,
  splitFenceLines,
  type FenceLine,
  type OpenFence,
} from "@/lib/fence-tracking"

/** Fence info-strings the Dash canvas compiler consumes as a dashboard spec. */
export const DASH_SPEC_LANGUAGES = [
  "yaml-render",
  "yaml-patch",
  "yaml-edit",
  "yaml-diff",
  "json-render",
  "json-render-stream",
] as const

export type DashSpecLanguage = (typeof DASH_SPEC_LANGUAGES)[number]

export type DashSpecFence = {
  lang: DashSpecLanguage
  complete: boolean
}

export type StripDashSpecFencesResult = {
  displayText: string
  fences: DashSpecFence[]
}

const SPEC_LANG_SET = new Set<string>(DASH_SPEC_LANGUAGES)

const REPLACE_LANGS = new Set<DashSpecLanguage>([
  "yaml-render",
  "json-render",
  "json-render-stream",
])

function infoLang(info: string): string {
  const token = info.trim().split(/[^a-z0-9-]/i)[0] ?? ""
  return token.toLowerCase()
}

function canonicalizeSpecLang(lang: string): DashSpecLanguage | null {
  if (SPEC_LANG_SET.has(lang)) return lang as DashSpecLanguage
  if (!lang.includes("-")) return null
  const match = DASH_SPEC_LANGUAGES.find((candidate) => candidate.startsWith(lang))
  return match ?? null
}

function joinKeptLines(lines: FenceLine[], keep: boolean[]): string {
  let text = ""
  for (let i = 0; i < lines.length; i++) {
    if (!keep[i]) continue
    text += lines[i].content + lines[i].separator
  }
  return text
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+/, "")
    .replace(/\n+$/, "")
}

/**
 * Remove Dash canvas spec fences from markdown meant for the chat bubble.
 *
 * Open and closed `yaml-render` / `yaml-patch` / … blocks are omitted so the
 * SQL inside `_sql: |` never renders as a growing code block. Callers must
 * keep the raw markdown for canvas extraction — this is display-only.
 */
export function stripDashSpecFences(markdown: string): StripDashSpecFencesResult {
  if (!markdown) return { displayText: markdown, fences: [] }

  const lines = splitFenceLines(markdown)
  const keep = Array.from({ length: lines.length }, () => true)
  const fences: DashSpecFence[] = []

  let i = 0
  while (i < lines.length) {
    const line = lines[i].content
    const match = getFenceMatch(line)
    if (!match || !canOpenFence(line, match)) {
      i += 1
      continue
    }

    const lang = canonicalizeSpecLang(infoLang(match[4]))
    if (!lang) {
      i += 1
      continue
    }

    const open: OpenFence = { lineIndex: i, marker: match[2] }
    let end = lines.length - 1
    let complete = false
    for (let j = i + 1; j < lines.length; j++) {
      const closer = getFenceMatch(lines[j].content)
      if (closer && closesFence(open, lines[j].content, closer)) {
        end = j
        complete = true
        break
      }
    }

    fences.push({ lang, complete })
    for (let k = i; k <= end; k++) keep[k] = false
    i = end + 1
  }

  return { displayText: joinKeptLines(lines, keep), fences }
}

export function isDashChatModule(moduleName: string | undefined): boolean {
  return moduleName === "Dash"
}

/** i18n key for the last stripped spec fence, or null when none.
 * `streamComplete` treats an unclosed last fence as complete for the status
 * key only — the spec body stays hidden by `stripDashSpecFences`.
 */
export function dashSpecStatusI18nKey(
  fences: DashSpecFence[],
  streamComplete = false
): string | null {
  if (fences.length === 0) return null
  const last = fences[fences.length - 1]
  if (!last.complete && !streamComplete) {
    return REPLACE_LANGS.has(last.lang) ? "dash.chat.spec_building" : "dash.chat.spec_updating"
  }
  return "dash.chat.spec_updated"
}
